import { useEffect, useRef, useState } from 'react';
import { saveStatus, type SaveStatus } from '../core/documents';
import { useApp, useSession, useUi, useWindow } from '../app/context';
import { patchUi, setRendered } from '../app/ui';
import { setBlockStyle, toggleInline, toggleList, toggleQuote, type BlockStyle } from '../editor/markdownCommands';
import { BoldIcon, BulletListIcon, CheckIcon, ChevronIcon, CodeIcon, EyeIcon, ItalicIcon, LinkIcon, OrderedListIcon, QuoteIcon, SourceIcon, StrikeIcon } from './icons';
import { iconGesture } from './iconMotion';
import { shortcutLabel } from './keys';
import { MenuItem, Popover, menuKeys, usePresence } from './Popover';
import { LinkField, Tool, toolbarKeys, useCommand, useLamp, useMorphWidth, useRovingStart } from './pillParts';

/**
 * The primary pill: the instrument's one control object, floating low in the
 * editor. For Markdown it carries formatting; for every document it carries
 * the save state — which only appears when there is something to save, says
 * "Saved" for a moment, then folds away. It recedes while the person types.
 */

const BLOCK_LABEL = ['¶', 'H1', 'H2', 'H3'];
const BLOCK_NAME = ['Text', 'Heading 1', 'Heading 2', 'Heading 3'];

function useSaveSegment(docId: string | null, status: SaveStatus | null) {
  const [flash, setFlash] = useState<'saved' | 'downloaded' | null>(null);
  const [prev, setPrev] = useState({ docId, status });
  if (prev.docId !== docId || prev.status !== status) {
    const justSaved = prev.docId === docId && (prev.status === 'draft' || prev.status === 'edited') && (status === 'saved' || status === 'downloaded');
    setPrev({ docId, status });
    setFlash(justSaved ? (status as 'saved' | 'downloaded') : null);
  }
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 2200);
    return () => clearTimeout(t);
  }, [flash]);
  const unsaved = status === 'draft' || status === 'edited';
  return { visible: unsaved || flash !== null, unsaved, flash };
}

export function Pill({ compact, focused }: { compact: boolean; focused: boolean }) {
  const app = useApp();
  const activeId = useWindow((w) => w.activeId);
  const doc = useSession((s) => s.docs.find((d) => d.id === activeId) ?? null);
  const typing = useUi((s) => s.typing);
  const linkEditor = useUi((s) => s.linkEditor);
  const status = doc ? saveStatus(doc) : null;
  const save = useSaveSegment(doc?.id ?? null, status);
  const md = doc?.kind === 'md';
  const rendered = useUi((s) => md && !!doc && s.rendered.has(doc.id));
  const linking = linkEditor === 'primary' && md && !rendered;
  // The controls live in the focused window only; they hand over when focus moves.
  const show = focused && !!doc && (md || save.visible);
  const saveRef = useRef<HTMLButtonElement>(null);
  // Written for real: the check draws itself.
  useEffect(() => {
    if (save.flash) iconGesture(saveRef.current, 'save');
  }, [save.flash]);
  const { mounted, closing } = usePresence(show, 180);
  const entering = useEntering(mounted);

  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const lamp = useRef<HTMLDivElement>(null);
  useMorphWidth(outer, inner, mounted);
  useLamp(outer, lamp);
  useRovingStart(outer);

  if (!mounted) return null;

  return (
    <div className="pill-dock" data-receded={(typing && !linking) || undefined}>
      <div
        ref={outer}
        className="pill"
        role="toolbar"
        aria-label={md ? 'Formatting and saving' : 'Saving'}
        data-state={closing ? 'closing' : 'open'}
        data-entering={entering || undefined}
        onKeyDown={toolbarKeys}
        onFocus={() => patchUi({ typing: false })}
      >
        <div className="pill-lamp" ref={lamp} aria-hidden="true" />
        <div className="pill-inner" ref={inner}>
          {md && !linking && !rendered && <FormatTools compact={compact} />}
          {rendered && doc && <RenderedTools docId={doc.id} />}
          {linking && <LinkField />}
          {save.visible && !linking && (
            <>
              {md && <span className="pill-sep" aria-hidden="true" />}
              <button
                ref={saveRef}
                type="button"
                className="save-btn"
                data-tool
                data-lamp
                tabIndex={-1}
                data-state={save.unsaved ? 'unsaved' : 'saved'}
                aria-label={save.unsaved ? (status === 'draft' ? 'Save — not yet saved to a file' : 'Save — unsaved changes') : save.flash === 'downloaded' ? 'Downloaded a copy' : 'Saved to file'}
                data-tip={save.unsaved ? (status === 'draft' ? 'Not yet saved to a file' : doc?.origin === 'file' ? 'Unsaved changes' : 'Download a copy') : undefined}
                data-kbd={save.unsaved ? shortcutLabel('Mod-s') : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => save.unsaved && doc && app.save(doc.id)}
              >
                <span className="save-mark" aria-hidden="true">
                  {save.unsaved ? <span className="save-dot" /> : <CheckIcon size={14} />}
                </span>
                <span className="save-label" key={save.unsaved ? 'save' : (save.flash ?? 'saved')}>
                  {save.unsaved ? 'Save' : save.flash === 'downloaded' ? 'Downloaded' : 'Saved'}
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** True for the first moments after the toolbar appears, while its contents settle in. */
function useEntering(mounted: boolean): boolean {
  const [entering, setEntering] = useState(mounted);
  const [prev, setPrev] = useState(mounted);
  if (mounted !== prev) {
    setPrev(mounted);
    setEntering(mounted);
  }
  useEffect(() => {
    if (!entering) return;
    const t = setTimeout(() => setEntering(false), 600);
    return () => clearTimeout(t);
  }, [entering]);
  return entering;
}

/** In a rendered document the toolbar says so, and offers the way back to the source in one step. */
function RenderedTools({ docId }: { docId: string }) {
  const app = useApp();
  return (
    <>
      <span className="rendered-status" role="status">
        <EyeIcon size={16} />
        <span>Rendered</span>
        <span className="rendered-status-note">read-only</span>
      </span>
      <span className="pill-sep" aria-hidden="true" />
      <button
        type="button"
        className="source-btn"
        data-tool
        data-lamp
        tabIndex={-1}
        data-tip="Back to the Markdown source"
        data-kbd="Esc"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setRendered(docId, false);
          app.focusEditor();
        }}
      >
        <SourceIcon size={16} />
        <span>Edit Source</span>
      </button>
    </>
  );
}

function FormatTools({ compact }: { compact: boolean }) {
  const format = useUi((s) => s.format);
  const run = useCommand();
  const [styleOpen, setStyleOpen] = useState(false);
  const [styleEl, setStyleEl] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <Tool
        label="Text style"
        className="tool-style"
        haspopup
        expanded={styleOpen}
        buttonRef={setStyleEl}
        onPress={() => setStyleOpen((o) => !o)}
      >
        <span className="style-glyph" data-level={format.block} key={format.block}>
          {BLOCK_LABEL[Math.min(format.block, 3)] ?? 'H'}
        </span>
        <ChevronIcon size={12} className="style-chevron" />
      </Tool>
      <span className="pill-sep" aria-hidden="true" />
      <Tool label="Bold" gesture="bold" kbd={shortcutLabel('Mod-b')} pressed={format.bold} onPress={() => run((s) => toggleInline(s, 'bold'))}>
        <BoldIcon />
      </Tool>
      <Tool label="Italic" gesture="italic" kbd={shortcutLabel('Mod-i')} pressed={format.italic} onPress={() => run((s) => toggleInline(s, 'italic'))}>
        <ItalicIcon />
      </Tool>
      <Tool label="Strikethrough" gesture="strike" kbd={shortcutLabel('Mod-Shift-x')} pressed={format.strike} onPress={() => run((s) => toggleInline(s, 'strike'))}>
        <StrikeIcon />
      </Tool>
      <Tool label="Code" gesture="code" kbd={shortcutLabel('Mod-e')} pressed={format.code} onPress={() => run((s) => toggleInline(s, 'code'))}>
        <CodeIcon />
      </Tool>
      {!compact && (
        <>
          <span className="pill-sep" aria-hidden="true" />
          <Tool label="Bulleted list" gesture="bullet" kbd={shortcutLabel('Mod-Shift-8')} pressed={format.list === 'bullet'} onPress={() => run((s) => toggleList(s, 'bullet'))}>
            <BulletListIcon />
          </Tool>
          <Tool label="Numbered list" gesture="ordered" kbd={shortcutLabel('Mod-Shift-7')} pressed={format.list === 'ordered'} onPress={() => run((s) => toggleList(s, 'ordered'))}>
            <OrderedListIcon />
          </Tool>
          <Tool label="Quote" gesture="quote" kbd={shortcutLabel('Mod-Shift-9')} pressed={format.quote} onPress={() => run(toggleQuote)}>
            <QuoteIcon />
          </Tool>
        </>
      )}
      <span className="pill-sep" aria-hidden="true" />
      <Tool label="Link" gesture="link" kbd={shortcutLabel('Mod-k')} pressed={format.link} onPress={() => patchUi({ linkEditor: 'primary', typing: false })}>
        <LinkIcon />
      </Tool>
      <StyleMenu open={styleOpen} anchor={styleEl} compact={compact} onClose={() => setStyleOpen(false)} />
    </>
  );
}

function StyleMenu({ open, anchor, compact, onClose }: { open: boolean; anchor: HTMLElement | null; compact: boolean; onClose: () => void }) {
  const app = useApp();
  const format = useUi((s) => s.format);
  const run = useCommand();
  const pick = (fn: () => void) => () => {
    onClose();
    fn();
    app.focusEditor();
  };
  return (
    <Popover open={open} anchor={anchor} placement="above-start" onClose={onClose} label="Text style" role="menu" className="style-popover">
      <div className="menu" onKeyDown={menuKeys}>
        {([0, 1, 2, 3] as BlockStyle[]).map((level) => (
          <MenuItem
            key={level}
            className={`style-item style-item-${level}`}
            label={BLOCK_NAME[level]}
            hint={shortcutLabel(`Mod-Alt-${level}`)}
            checked={format.block === level && !format.list && (level !== 0 || !format.quote)}
            onSelect={pick(() => run((s) => setBlockStyle(s, level)))}
          />
        ))}
        {compact && (
          <>
            <div className="menu-sep" role="separator" />
            <MenuItem icon={<BulletListIcon size={16} />} label="Bulleted list" checked={format.list === 'bullet'} onSelect={pick(() => run((s) => toggleList(s, 'bullet')))} />
            <MenuItem icon={<OrderedListIcon size={16} />} label="Numbered list" checked={format.list === 'ordered'} onSelect={pick(() => run((s) => toggleList(s, 'ordered')))} />
            <MenuItem icon={<QuoteIcon size={16} />} label="Quote" checked={format.quote} onSelect={pick(() => run(toggleQuote))} />
          </>
        )}
      </div>
    </Popover>
  );
}
